import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useRef,
  useEffect,
  ChangeEventHandler,
} from "react";
import { Link, useParams } from "react-router-dom";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import {
  startUpload,
  uploadSuccess,
  uploadFailure,
} from "../../store/imageSlice";
import { toast } from "react-toastify";
import apiClient from "../../api/apiClient";
import moment from "moment";
import { fetchUserData } from "../../store/UserSlice";
import { RootState } from "../../store/store";
import { IHealthCondition } from "../../interfaces/HealthCondition";
import { useTranslation } from "react-i18next";

const ProgramBasicsEdit = ({ chnageTab, updateHasCounselling }: any) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const currentDate = new Date();
  const dispatch = useDispatch<any>();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState<any>(null);
  const [cost, setCost] = useState("");
  const [slot, setSlot] = useState("");
 // const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const [participants, setParticipants] = useState("");
  const [zoom, setZoom] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [images, setImages] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [errors, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [programGet, setprogramGet] = useState<any | null>(null);
  const [healthCondition, setHealthCondition] = useState("");
  const [counsellingLink, setcounsellingLink] = useState("");
  const [hasCounselling, setHasCounselling] = useState(true);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate maximum file size (1MB)
    if (file.size > 1024 * 1024) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      const sizeError = t("adminPrograms.basicForm.imageTooLarge", "The image file is too large (max 1MB). Please choose a smaller image.");
      setError(sizeError);
      toast.error(sizeError);
      return;
    }

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/svg+xml", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      const typeError = t("adminPrograms.basicForm.unsupportedFormat", "Unsupported format. Please select a PNG, JPG, or SVG file.");
      setError(typeError);
      toast.error(typeError);
      return;
    }

    setSelectedImage(file);
    setImage(file);
    setError(null);
  };
  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };
  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };
  const handleDateChange = (dateToChange: DateObject | DateObject[] | null) => {
    setDate(moment(dateToChange?.toString()).toDate());
  };
  const handleCostChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCost(e.target.value);
  };
  const handleslotChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSlot(e.target.value);
  };
  const handlepesChange = (e: ChangeEvent<HTMLInputElement>) => {
    setParticipants(e.target.value);
  };
  const handlezoomChange = (e: ChangeEvent<HTMLInputElement>) => {
    setZoom(e.target.value);
  };
  const handleHealthCondition: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setHealthCondition(e.target.value);
  };
 
  const handlecounsellingLinkChange = (e: ChangeEvent<HTMLInputElement>) => {
    setcounsellingLink(e.target.value);
  };
  const handleHasCounsellingChange = (e: ChangeEvent<HTMLInputElement>) => {
    setHasCounselling(e.target.checked);
    if (updateHasCounselling) updateHasCounselling(e.target.checked);
  };
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Dispatch startUpload action
    dispatch(startUpload());

    try {
      const formData = new FormData();
      const isValidDate = date instanceof Date && !isNaN(date.getTime());
      formData.append("startDate", isValidDate ? date.toISOString() : (date ? new Date(date).toISOString() : ""));
      formData.append("maxParticipants", participants);
      formData.append("totalCounsellings", slot);
      formData.append("zoomLink", zoom);
      formData.append("description", description);
      formData.append("startTime", moment().toString());
      formData.append("endTime", moment().toString());
      formData.append("title", title);
      formData.append("cost", cost);
      formData.append("healthCondition", healthCondition);
      formData.append("counsellingLink", counsellingLink);
      formData.append("hasCounselling", String(hasCounselling));
      if (selectedImage) {
        formData.append("icon", selectedImage);
      }
      const response = await apiClient.put(
        `/api/v1/programs/${id}`,
        formData
      );

      dispatch(uploadSuccess(response.data.imageUrl));
      if (response.data.status) {
        toast.info(t("adminPrograms.basicForm.updatedSuccess", "Successfully Updated"));
        chnageTab(1);
      }
    } catch (error: any) {
      const msg =
        error.response?.status === 413
          ? t("adminPrograms.basicForm.imageTooLarge", "The image file is too large (max 1MB). Please upload a smaller image.")
          : error.response?.data?.error?.message ||
            error.response?.data?.message ||
            error.message ||
            t("common.error", "An error occurred");
      toast.error(msg);
      dispatch(uploadFailure(msg));
    }
  };  

  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        if (!id) return;
        const response = await apiClient.get(`/api/v1/programs/${id}`);

        setprogramGet(response?.data?.data);

        // Set the title with the fetched data
        const startDateString = response.data.data.startDate || "";
        const initialDate = startDateString ? new Date(startDateString) : null;
        
        setDate(initialDate);
        setTitle(response.data.data.title || ""); // Assuming the name field holds the title
        setImages(response.data.data.icon);
        setDescription(response.data.data.description || "");
        setCost(response.data.data.cost || "");
        setSlot(response.data.data.totalCounsellings || "");
        setParticipants(response.data.data.maxParticipants || "");
        setZoom(response.data.data.zoomLink || "");
        setHealthCondition(response.data.data.healthCondition || "");
        localStorage.setItem('startDate',response?.data?.data?.startDate);
        setcounsellingLink(response.data.data.counsellingLink || "");
        setHasCounselling(response.data.data.hasCounselling !== false);
        //setSelectedTime(moment(response.data.data.startTime).toDate());
      } catch (error) {
        console.error("Error fetching health condition details:", error);
      }
    };

    if (id) {
      fetchProgramDetails();
    }
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(fetchUserData());
  }, [dispatch]);

//  const parsedData = typeof data === "string" ? JSON.parse(data) : data;
  const parsedData: any = useSelector(
    (state: RootState) => state.user.data
  );

  const healthConditionsList: IHealthCondition[] = Array.isArray(parsedData)
    ? parsedData
    : (parsedData?.healthConditions || []);
  
  return (
    <div className="px-8 overflow-y-scroll max-h-[calc(100vh-220px)] scrollbar-hide pb-20">
      <form onSubmit={handleSubmit}>
        <div className="mt-4 mb-4">
          <input
            id="image"
            type="file"
            name="image"
            onChange={handleImageChange}
            className="hidden"
            accept="image/png, image/jpeg, image/jpg, image/svg+xml"
            ref={fileInputRef}
          />

          {selectedImage instanceof File || images ? (
            <div className="flex items-center gap-4">
              <div className="relative w-28 h-28 rounded-xl border-2 border-gray-200 bg-gray-50 flex items-center justify-center p-2 overflow-hidden shadow-xs">
                <img
                  src={selectedImage instanceof File ? URL.createObjectURL(selectedImage) : (images || "")}
                  alt="preview"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-gilroy-bold text-[#037897] bg-[#E8F5F8] hover:bg-[#d0eff5] active:scale-98 rounded-lg transition-all border border-[#037897]/25 shadow-xs"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span>{t("adminPrograms.basicForm.replaceImage", "Replace Image")}</span>
                </button>
                <span className="text-[11px] text-gray-400 font-gilroy">
                  PNG, JPG, SVG (Max 1MB)
                </span>
              </div>
            </div>
          ) : (
            <label
              htmlFor="image"
              className="flex flex-col items-center justify-center w-32 h-32 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex flex-col items-center justify-center p-3 text-center">
                <img
                  src="/images/gallery-add.png"
                  alt="upload"
                  className="w-7 h-7 mb-2 object-contain"
                />
                <p className="text-xs text-[#4C535F] font-gilroy-medium">
                  {t("adminPrograms.basicForm.uploadIcon", "Upload Icon")}
                </p>
                <p className="text-[10px] text-gray-400 mt-1">Max 1MB</p>
              </div>
            </label>
          )}

          {errors && <p className="text-xs text-red-500 mt-2 font-gilroy-medium">{errors}</p>}
        </div>
        <div className="flex flex-col md:flex-row">
        <div className="mb-4 w-full md:w-1/2  pr-4">
            <label
              className="block text-[#607D8B] text-sm  mb-2"
              htmlFor="title"
            >
              {t("adminPrograms.basicForm.title", "Title")}
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={title}
              onChange={handleTitleChange}
              className="border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 "
              placeholder={t("adminPrograms.basicForm.titlePlaceholder", "Please select your title")}
              required
            />
          </div>
          <div className="mb-4 w-full md:w-1/2  pr-4">
          <label className="block text-[#607D8B] text-sm mb-2" htmlFor="date">
              {t("adminPrograms.basicForm.healthCondition", "Health Condition")}
            </label>
            <select
                value={healthCondition}
                name="healthCondition"
                onChange={handleHealthCondition}
                className="border-2 h-11 text-[#263238] capitalize bg-[#EDF2F6] placeholder:text-[#8D98AA] font-[18px] focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 focus:outline-none focus:border-gray-500 w-full"
              >
                <option value="">{t("adminPrograms.basicForm.selectHealthCondition", "Select Health Condition")}</option>
                {healthConditionsList.map((condition: IHealthCondition) => (       
                  <option key={condition.id} value={condition.id}>
                    {condition.name}
                  </option>
                ))}

              </select>

          </div>

          
        </div>
        <div className="flex flex-col md:flex-row">      
          <div className="mb-4 w-full md:w-1/2  pr-4">
            <label className="block text-[#607D8B] text-sm mb-2" htmlFor="date">
              {t("adminPrograms.basicForm.date", "Date")}
            </label>
            <DatePicker
              placeholder={t("adminPrograms.basicForm.datePlaceholder", "MM/DD/YYYY")}
              inputClass="border-2 text-[#263238] bg-[#EDF2F6] placeholder:text-[#8D98AA] font-[18px] focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 focus:outline-none focus:border-gray-500 w-full"
              containerClassName="w-full"
              calendarPosition="bottom"
              id={`date`}
              required
              
              onChange={(date) => handleDateChange(date)}
              format="MM/DD/YYYY"
              value={moment(date).format("MM/DD/YYYY")}
              
            />
          </div>

          <div className="mb-4 w-full md:w-1/2  pr-4">
            <label
              className="block text-[#607D8B]  text-sm mb-2"
              htmlFor="title"
            >
              {t("adminPrograms.basicForm.cost", "Cost")}
            </label>
            <input
              type="text"
              id="cost"
              name="cost"
              value={cost}
              onChange={handleCostChange}
              className="border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 "
              placeholder={t("adminPrograms.basicForm.costPlaceholder", "Enter a Cost")}
              required
            />
          </div>

          <div className="mb-4 w-full md:w-1/2  pr-4">
            <label
              className="block text-[#607D8B] text-sm mb-2"
              htmlFor="title"
            >
              {t("adminPrograms.basicForm.maxParticipants", "Max participants")}
            </label>
            <input
              type="text"
              id="participants"
              name="participants"
              pattern="[0-9]*"
              value={participants}
              onChange={handlepesChange}
              className="border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 "
              placeholder={t("adminPrograms.basicForm.participantsPlaceholder", "Enter a participants")}
              required
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row mb-4">
          <div className="flex items-center w-full md:w-1/2 pr-4">
            <input
              type="checkbox"
              id="hasCounselling"
              name="hasCounselling"
              checked={hasCounselling}
              onChange={handleHasCounsellingChange}
              className="mr-2 h-4 w-4"
            />
            <label className="text-[#607D8B] text-sm" htmlFor="hasCounselling">
              {t("adminPrograms.basicForm.hasCounselling", "Has 1-1 Counselling")}
            </label>
          </div>
        </div>

        {hasCounselling && (
        <div className="flex flex-col md:flex-row">
          <div className="mb-4 w-full md:w-1/2 pr-4">
            <label
              className="block text-[#607D8B] text-sm  mb-2"
              htmlFor="title"
            >
              {t("adminPrograms.basicForm.totalCounsellings", "Total Counsellings")}
            </label>
            <input
              type="text"
              id="slot"
              name="slot"
              value={slot}
              pattern="[0-9]*"
              onChange={handleslotChange}
              className="border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 "
              placeholder={t("adminPrograms.basicForm.counsellingSlotsPlaceholder", "Enter a total Counselling slots")}
            />
          </div>
        </div>
        )}
        <div className="flex flex-col md:flex-col">
          <div className="mb-4 w-full  pr-4">
            <label
              className="block text-[#607D8B] text-sm  mb-2"
              htmlFor="title"
            >
              {t("adminPrograms.basicForm.zoomLink", "Zoom link")}
            </label>
            <input
              type="text"
              id="zoom"
              name="zoom"
              value={zoom}
              pattern="https?://.+"
              onChange={handlezoomChange}
              className="border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 "
              placeholder={t("adminPrograms.basicForm.zoomLinkPlaceholder", "Enter a zoom link")}
            />
          </div>
          {hasCounselling && (
          <div className="mb-4 w-full  pr-4">
            <label
              className="block text-[#607D8B] text-sm  mb-2"
              htmlFor="title"
            >
              {t("adminPrograms.basicForm.counsellingLink", "Counselling 1-1 Meeting link")}
            </label>
            <input
              type="text"
              id="counsellingLink"
              name="counsellingLink"
              value={counsellingLink}
              pattern="https?://.+"
              onChange={handlecounsellingLinkChange}
              className="border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 "
              placeholder={t("adminPrograms.basicForm.counsellingLinkPlaceholder", "Enter a Counselling 1-1 Meeting link")}
            />
          </div>
          )}

          <div className="mb-4 w-full  pr-4">
            <label
              className="block text-[#607D8B] text-sm mb-2"
              htmlFor="title"
            >
              {t("adminPrograms.basicForm.description", "Description")}
            </label>

            <textarea
              id="description"
              name="description"
              onChange={handleDescriptionChange}
              value={description}
              className="resize-none border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] px-3 py-2 "
              rows={4}
              placeholder={t("adminPrograms.basicForm.descriptionPlaceholder", "Enter a description")}
            >
              {description}
            </textarea>
          </div>
        </div>

        <button
          type="submit"
          className="bg-[#B4804A] text-white font-semibold float-right w-32 py-2 px-4 mb-8 rounded-md"
        >
          {t("adminPrograms.basicForm.save", "Save")}
        </button>
        <Link
          to="/admin/programs"
          className="bg-[#90A4AE] text-white float-right font-semibold w-32 py-2 px-4 mb-8 mr-6 text-center rounded-md"
        >
          {t("adminPrograms.basicForm.cancel", "Cancel")}
        </Link>
      </form>
    </div>
  );
};

export default ProgramBasicsEdit;
